import React, { useState } from 'react';
import { Label, TextInput } from 'flowbite-react';
import Select, { MultiValue } from 'react-select';
import { PreInterviewsFormData } from 'types';
import { Dropdown, expectedJoiningOptions, gradYearOptions, locationPreferenceOptions } from 'shared-utils';
import { checkMinMaxValidity } from 'src/utils/interview';
import { pifChangableFieldsConfig } from 'src/utils/pifConfig';
import { useUserContext } from 'src/context/UserProvider';

interface Props {
	formData: Partial<PreInterviewsFormData>;
	setFormData: (arg: Partial<PreInterviewsFormData>) => void;
	isValid: Boolean;
	setIsValid: (arg: Boolean) => void;
}

const FormIfGraduated = ({ formData, setFormData, isValid, setIsValid }: Props) => {
	const { user } = useUserContext();
	let locationTimeoutId: NodeJS.Timeout | undefined;
	const [isMoreThanFiveLocationSelected, setIsMoreThanFiveLocationSelected] = useState<boolean>(false);
	const handleLoactionPreferences = (value: MultiValue<Dropdown>) => {
		const selectedLocation = value.map(item => item.value);
		if (selectedLocation.length <= 5) {
			setIsMoreThanFiveLocationSelected(false);
			setFormData({
				...formData,
				preferredJobLocations: selectedLocation,
			});
		} else {
			setIsMoreThanFiveLocationSelected(true);
			if (locationTimeoutId) {
				clearTimeout(locationTimeoutId); // Clear the previous timeout
			}
			locationTimeoutId = setTimeout(() => {
				setIsMoreThanFiveLocationSelected(false);
			}, 3000);
		}
	};

	return (
		<div className="flex flex-col gap-4">
			<div>
				<div className="mt-2 font-inter">
					<Label>
						Graduation Year <span className="text-red-600">*</span>
					</Label>

					<Select
						isDisabled={pifChangableFieldsConfig.graduationYear === false && user?.profile.gradYear ? true : false}
						className="single-value-select-tag !max-w-[500px]"
						menuPlacement={'auto'}
						classNamePrefix="my-select-plans"
						instanceId="graduatedGradYear"
						closeMenuOnSelect
						options={gradYearOptions}
						value={gradYearOptions?.find(item => Number(item.value) === formData.graduationYear)}
						onChange={value => {
							if (value) {
								setFormData({
									...formData,
									graduationYear: Number(value?.value),
								});
							}
						}}
					/>
				</div>
			</div>

			<div className="flex flex-row gap-3">
				<div className="mt-2 font-inter">
					<Label>Last Company</Label>
					<TextInput
						disabled={
							pifChangableFieldsConfig?.lastCompany === false && user?.profile?.lastCompany !== undefined ? true : false
						}
						style={{
							fontFamily: "'Inter', sans-serif",
							fontSize: '14px',
							width: '500px',
						}}
						onChange={e => setFormData({ ...formData, lastCompany: e.target.value })}
						value={formData.lastCompany}
						placeholder="LevelUp"
					/>
				</div>

				<div className="mt-2 font-inter">
					<Label>Last Role</Label>
					<TextInput
						disabled={
							pifChangableFieldsConfig?.lastRole === false && user?.profile?.lastRole !== undefined ? true : false
						}
						style={{
							fontFamily: "'Inter', sans-serif",
							fontSize: '14px',
							width: '500px',
						}}
						onChange={e => setFormData({ ...formData, lastRole: e.target.value })}
						value={formData.lastRole}
						placeholder="Senior Backend Developer"
					/>
				</div>
			</div>

			<div className="flex gap-3">
				<div className="mt-2 font-inter">
					<Label>Last Drawn Salary (in LPA)</Label>
					<TextInput
						disabled={
							pifChangableFieldsConfig?.lastDrawnSalary === false && user?.profile?.lastDrawnSalary !== undefined
								? true
								: false
						}
						type="number"
						style={{
							fontFamily: "'Inter', sans-serif",
							fontSize: '14px',
							width: '500px',
						}}
						min={0}
						step={0.1}
						max={99}
						onChange={e => setFormData({ ...formData, lastDrawnSalary: Number(e.target.value) })}
						value={formData.lastDrawnSalary}
						placeholder="18"
					/>
				</div>
				<div className="mt-2 font-inter w-full">
					<Label>Expected Joining</Label>
					<Select
						isDisabled={
							pifChangableFieldsConfig?.expectedJoining === false && user?.profile?.expectedJoining !== undefined
								? true
								: false
						}
						className="single-value-select-tag !max-w-[500px]"
						menuPlacement={'auto'}
						classNamePrefix="my-select-plans"
						instanceId="graduatedExpectedJoining"
						closeMenuOnSelect
						options={expectedJoiningOptions}
						value={expectedJoiningOptions?.find(item => item.value === formData.expectedJoining)}
						onChange={value => {
							if (value) {
								setFormData({
									...formData,
									expectedJoining: value?.value,
								});
							}
						}}
					/>
				</div>
			</div>

			<div className="mt-2 font-inter">
				<div className="mt-2 font-inter">
					<Label>
						Preferred Location (upto 5 locations) <span className="text-red-600">*</span>
					</Label>
				</div>
				{isMoreThanFiveLocationSelected ? (
					<p className="font-inter text-[12px] text-red-700">You can only Select upto 5 locations</p>
				) : null}
				<Select
					isDisabled={
						pifChangableFieldsConfig?.preferredJobLocations === false &&
						user?.profile?.preferredJobLocations !== undefined &&
						user?.profile?.preferredJobLocations?.length > 0
							? true
							: false
					}
					menuPlacement={'auto'}
					styles={{
						multiValue: baseStyles => ({
							...baseStyles,
							backgroundColor: '#111E2C',
						}),
					}}
					menuPosition="fixed"
					classNamePrefix="my-select-plans"
					instanceId="location_preference"
					className="!max-w-[500px]"
					isMulti={true}
					placeholder={'Select your prefered locations'}
					value={locationPreferenceOptions
						?.filter(location => formData.preferredJobLocations?.includes(location))
						.map(location => ({ value: location, label: location }))}
					closeMenuOnSelect={false}
					options={locationPreferenceOptions.map(location => ({ value: location, label: location }))}
					onChange={handleLoactionPreferences}
				/>
			</div>
		</div>
	);
};

export default FormIfGraduated;
